/**
 * Send-while-running helpers.
 *
 * Talkyn needs three user-facing behaviors when Lars sends another message
 * while a reply is already in flight:
 * - queue normally
 * - steer the current reply
 * - interrupt and replace
 *
 * The gateway already understands queue steering via `/queue steer`, but that
 * transport marker should not leak into Talkyn's displayed chat history.
 */

export type SendWhileRunningMode = 'queue' | 'steer' | 'interrupt';
export type SendWhileRunningTransport = 'chat.send' | 'sessions.steer';

export interface SendWhileRunningPlan {
  mode: SendWhileRunningMode;
  transport: SendWhileRunningTransport;
  transportMessage: string;
  createAssistantPlaceholder: boolean;
  steersCurrentReply: boolean;
  fallbackReason?: 'inactive' | 'not_streaming' | 'unsafe_formatting';
}

export interface PlanSendWhileRunningParams {
  message: string;
  mode?: string | null;
  isReplyActive: boolean;
  isReplyStreaming: boolean;
}

const QUEUE_DIRECTIVE = '/queue';
const KNOWN_QUEUE_MODES = new Set([
  'queue',
  'collect',
  'followup',
  'steer',
  'steer-backlog',
  'steer+backlog',
  'interrupt',
]);

export function normalizeSendWhileRunningMode(value?: string | null): SendWhileRunningMode {
  switch ((value ?? '').trim().toLowerCase()) {
    case 'steer':
      return 'steer';
    case 'interrupt':
      return 'interrupt';
    default:
      return 'queue';
  }
}

/**
 * The inline `/queue steer` transport path currently normalizes whitespace.
 * Keep it for plain spoken / single-line follow-ups, and fall back to the
 * normal queued send path for multiline text so we never mangle formatting.
 */
export function canUseInlineSteerDirective(message: string): boolean {
  return !/[\r\n\t]/.test(message);
}

export function planSendWhileRunning(params: PlanSendWhileRunningParams): SendWhileRunningPlan {
  const mode = normalizeSendWhileRunningMode(params.mode);

  if (mode === 'interrupt' && params.isReplyActive) {
    return {
      mode,
      transport: 'sessions.steer',
      transportMessage: params.message,
      createAssistantPlaceholder: true,
      steersCurrentReply: false,
    };
  }

  if (mode === 'steer') {
    if (!params.isReplyActive) {
      return {
        mode,
        transport: 'chat.send',
        transportMessage: params.message,
        createAssistantPlaceholder: true,
        steersCurrentReply: false,
        fallbackReason: 'inactive',
      };
    }

    if (!params.isReplyStreaming) {
      return {
        mode,
        transport: 'chat.send',
        transportMessage: params.message,
        createAssistantPlaceholder: true,
        steersCurrentReply: false,
        fallbackReason: 'not_streaming',
      };
    }

    if (!canUseInlineSteerDirective(params.message)) {
      return {
        mode,
        transport: 'chat.send',
        transportMessage: params.message,
        createAssistantPlaceholder: true,
        steersCurrentReply: false,
        fallbackReason: 'unsafe_formatting',
      };
    }

    return {
      mode,
      transport: 'chat.send',
      transportMessage: `${QUEUE_DIRECTIVE} steer ${params.message}`,
      createAssistantPlaceholder: false,
      steersCurrentReply: true,
    };
  }

  return {
    mode,
    transport: 'chat.send',
    transportMessage: params.message,
    createAssistantPlaceholder: true,
    steersCurrentReply: false,
  };
}

function isWhitespace(char: string): boolean {
  return /\s/.test(char);
}

function isKnownQueueOption(token: string): boolean {
  return /^debounce[:=]/i.test(token) || /^cap[:=]/i.test(token) || /^drop[:=]/i.test(token);
}

/**
 * Hide leading `/queue ...` transport directives from rendered user messages.
 * Standalone queue commands stay visible so explicit session-setting commands
 * still make sense in the transcript.
 */
export function stripQueueDirectiveForDisplay(message: string): string {
  const trimmedStart = message.trimStart();
  const leadingWhitespaceLength = message.length - trimmedStart.length;

  if (!trimmedStart.toLowerCase().startsWith(QUEUE_DIRECTIVE)) {
    return message;
  }

  let index = leadingWhitespaceLength + QUEUE_DIRECTIVE.length;
  const nextChar = message[index] ?? '';
  if (nextChar && !isWhitespace(nextChar) && nextChar !== ':') {
    return message;
  }

  while (index < message.length && (isWhitespace(message[index]) || message[index] === ':')) {
    index += 1;
  }

  let consumedDirectiveArg = false;
  while (index < message.length) {
    const tokenStart = index;
    while (index < message.length && !isWhitespace(message[index])) {
      index += 1;
    }
    const token = message.slice(tokenStart, index);
    const normalized = token.toLowerCase();

    if (!token) {
      break;
    }

    if (KNOWN_QUEUE_MODES.has(normalized) || isKnownQueueOption(token)) {
      consumedDirectiveArg = true;
      while (index < message.length && isWhitespace(message[index])) {
        index += 1;
      }
      continue;
    }

    index = tokenStart;
    break;
  }

  if (!consumedDirectiveArg) {
    return message;
  }

  const remainder = message.slice(index);
  if (!remainder.trim()) {
    return message;
  }

  return `${message.slice(0, leadingWhitespaceLength)}${remainder.trimStart()}`;
}
